import 'react-loading-skeleton/dist/skeleton.css';

import { CenterItem, Column } from '@components/LinearLayout';
import { AgendaSpeaker } from '@hubs101/booking-api-skd-client/lib/types';
import { getImageSrc, getSpeakerFullName } from '@utils/fn';
import { useMemo, useState } from 'react';
import {
  CompanyLogo,
  FurtherInformation,
  FurtherInformationText,
  Name,
  ProfilePhoto,
  SpeakerDetails,
  TopSideSpeaker,
} from './styles';

import Icon from '@components/Icon';
import { useMobileWidth, useTabletWidth } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getEventDetails } from '@store/slices/eventSlice';

interface SpeakerItemProps {
  speaker: AgendaSpeaker;
  color: string;
  isAgendaLoading: boolean;
  eventSlug: string;
  sourceEventIdForPrevious?: string;
}

const SpeakerItem = ({
  speaker,
  color,
  isAgendaLoading,
  eventSlug,
  sourceEventIdForPrevious,
}: SpeakerItemProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = useTabletWidth();
  const isMobile = useMobileWidth();
  const navigate = useNavigate();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [linkHovered, setLinkHovered] = useState(false);

  const speakerFullName = useMemo(
    () => getSpeakerFullName(speaker?.first_name, speaker?.last_name),
    [speaker?.first_name, speaker?.last_name]
  );

  const redirectToSpeakerDetailsPage = () => {
    if (!speaker?.profile) return;

    const profileId: string =
      speaker.profile ?? (speaker as { profile_id?: string }).profile_id;

    const slug = eventSlug || sluggedEvent?.slug || '';

    const basePath = `/events/${slug}${t('speakers-url')}/${profileId}`;

    const url = sourceEventIdForPrevious
      ? `${basePath}?sourceEvent=${sourceEventIdForPrevious}`
      : basePath;

    navigate(url);
  };

  return (
    <article>
      <Column>
        <TopSideSpeaker>
          {speaker?.images?.picture?.length > 0 && (
            <Column $gap="0">
              <ProfilePhoto
                src={getImageSrc(speaker?.images?.picture)}
                alt={speakerFullName}
                loading="lazy"
                onClick={() => {
                  redirectToSpeakerDetailsPage();
                }}
              />
              {!!speaker?.company?.logo &&
                speaker?.company?.logo?.length > 0 && (
                  <div>
                    {isAgendaLoading ? (
                      <Skeleton
                        enableAnimation
                        width={isTablet ? '14rem' : '19rem'}
                        height="10rem"
                      />
                    ) : (
                      <CompanyLogo
                        src={getImageSrc(speaker?.company?.logo)}
                        alt={speaker?.company?.name}
                        loading="lazy"
                      />
                    )}
                  </div>
                )}
            </Column>
          )}

          <Column
            $gap={isMobile ? '0.8rem' : '1.2rem'}
            $justifyContent="flex-start"
            margin={`${isMobile ? '1rem' : '2rem'} 0 0 0`}
          >
            <Name>{speakerFullName}</Name>

            <Column $gap="0">
              <SpeakerDetails>{speaker?.job?.title}</SpeakerDetails>
              <SpeakerDetails>{speaker?.company?.name}</SpeakerDetails>
            </Column>

            <FurtherInformation
              $gap="0.5rem"
              onMouseEnter={() => { setLinkHovered(true); }}
              onMouseLeave={() => { setLinkHovered(false); }}
            >
              <CenterItem>
                <Icon
                  size={24}
                  color={linkHovered ? theme.color.mobilityGrey : color}
                  indentLess
                  name="external-link-line"
                />
              </CenterItem>
              <FurtherInformationText
                $color={color}
                onClick={() => {
                  redirectToSpeakerDetailsPage();
                }}
              >
                {t('further-information')}
              </FurtherInformationText>
            </FurtherInformation>
          </Column>
        </TopSideSpeaker>
      </Column>
    </article>
  );
};

export default SpeakerItem;
